import * as React from 'react';
import BPromise from 'bluebird';
import { AppUser } from 'Src/views/App/App';
import { History, Location } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { AwardGrantRole, AwardType } from 'Src/helpers/service/admin/awards';
import {
  createAwardType,
  deleteAwardType,
  getAllAwardTypes,
  updateAwardType
} from 'Src/helpers/service/admin/awardTypes';
import { uploadImage } from 'Src/helpers/service/image';
import { ensureAbsoluteUri } from 'Src/helpers/link/link';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import { faAward } from '@fortawesome/free-solid-svg-icons';
import {
  PeopleFilterChipOption,
  PeopleFilterChipSelect
} from 'Src/views/GenericView/AdminView/Users/PeopleFilterChipSelect';
import './AwardTypes.css';

const PLACEHOLDER = '/dist/images/empty_pic_uploader_box.png';
const ROLE_OPTIONS: AwardGrantRole[] = ['ADMIN', 'MANAGER', 'TEACHER', 'COACH'];
const ROLE_LABELS: Record<AwardGrantRole, string> = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  TEACHER: 'Teacher',
  COACH: 'Coach'
};

const PAGE_SIZE = 20;
const COLUMN_VISIBILITY_KEY = 'adminConsole.awardTypes';

type AwardColumnKey = 'award' | 'points' | 'status' | 'roles' | 'settings' | 'updated';
type AwardSortKey = 'name' | 'points' | 'status' | 'updated';

interface AwardTypeFilters {
  status: string[];
  points: string[];
  settings: string[];
  roles: string[];
}

const EMPTY_FILTERS: AwardTypeFilters = {
  status: [],
  points: [],
  settings: [],
  roles: []
};

const TABLE_COLUMNS: Array<{ key: AwardColumnKey; label: string; locked?: boolean }> = [
  { key: 'award', label: 'Award', locked: true },
  { key: 'points', label: 'Default points' },
  { key: 'status', label: 'Status' },
  { key: 'roles', label: 'Grantable by' },
  { key: 'settings', label: 'Settings' },
  { key: 'updated', label: 'Updated' }
];

interface State {
  items: AwardType[];
  search: string;
  searchDraft: string;
  selectedIds: string[];
  activeId: string | null;
  draft: Partial<AwardType> | null;
  isCreateMode: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isUploading: boolean;
  isDeleteModalOpen: boolean;
  submitError: string | null;
  loadError: string | null;
  toast: string | null;
  filters: AwardTypeFilters;
  draftFilters: AwardTypeFilters;
  isFiltersOpen: boolean;
  hiddenColumns: string[];
  isColumnsOpen: boolean;
  currentPage: number;
  sortKey: AwardSortKey;
  sortDirection: 'ASC' | 'DESC';
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class AwardTypes extends React.Component<Props, State> {
  toastTimer: any;
  columnsRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      search: '',
      searchDraft: '',
      selectedIds: [],
      activeId: null,
      draft: null,
      isCreateMode: false,
      isLoading: true,
      isSaving: false,
      isUploading: false,
      isDeleteModalOpen: false,
      submitError: null,
      loadError: null,
      toast: null,
      filters: { ...EMPTY_FILTERS },
      draftFilters: { ...EMPTY_FILTERS },
      isFiltersOpen: false,
      hiddenColumns: loadHiddenColumns(props.user, COLUMN_VISIBILITY_KEY).filter(field =>
        TABLE_COLUMNS.some(column => column.key === field && !column.locked)
      ),
      isColumnsOpen: false,
      currentPage: 1,
      sortKey: 'name',
      sortDirection: 'ASC'
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.onDocumentMouseDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
    this.setItems();
  }

  componentWillUnmount() {
    clearTimeout(this.toastTimer);
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (this.state.isColumnsOpen && this.columnsRef.current && !this.columnsRef.current.contains(target)) {
      this.setState({ isColumnsOpen: false });
    }
  };

  onDocumentKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (this.state.isDeleteModalOpen) {
      this.setState({ isDeleteModalOpen: false });
    } else if (this.state.draft) {
      this.closeDrawer();
    } else if (this.state.isFiltersOpen || this.state.isColumnsOpen) {
      this.setState({ isFiltersOpen: false, isColumnsOpen: false });
    }
  };

  setItems = () => {
    this.setState({ isLoading: true, loadError: null });
    getAllAwardTypes(this.props.user)
      .then(items => {
        this.setState({
          items,
          isLoading: false,
          loadError: null,
          currentPage: 1
        });
      })
      .catch(error => {
        const status = error && error.response && error.response.status;
        this.setState({
          isLoading: false,
          loadError: status
            ? `Award types could not be loaded (server error ${status}).`
            : 'Award types could not be loaded. Check your connection and try again.'
        });
      });
  };

  getFilteredItems() {
    const search = this.state.search.trim().toLowerCase();
    const { filters } = this.state;

    return this.state.items.filter(item => {
      const name = (item.name || '').toLowerCase();
      const description = (item.description || '').toLowerCase();
      if (search && !name.includes(search) && !description.includes(search)) return false;

      if (filters.status.length > 0) {
        const status = item.isEnabled ? 'enabled' : 'disabled';
        if (!filters.status.includes(status)) return false;
      }

      if (filters.points.length > 0) {
        const points = Number(item.defaultHousePoints || 0);
        const pointType = points > 0 ? 'positive' : points < 0 ? 'negative' : 'zero';
        if (!filters.points.includes(pointType)) return false;
      }

      if (filters.settings.length > 0) {
        const hasSelectedSetting = filters.settings.some(setting => {
          if (setting === 'peer') return Boolean(item.allowPeerNomination);
          if (setting === 'negative') return Boolean(item.allowNegative);
          return false;
        });
        if (!hasSelectedSetting) return false;
      }

      if (filters.roles.length > 0) {
        const roles = item.allowedRoles || [];
        if (!filters.roles.some(role => roles.includes(role as AwardGrantRole))) return false;
      }

      return true;
    });
  }

  getSortedItems(): AwardType[] {
    const { sortKey, sortDirection } = this.state;
    const direction = sortDirection === 'ASC' ? 1 : -1;

    return this.getFilteredItems()
      .slice()
      .sort((left, right) => {
        let leftValue: string | number = '';
        let rightValue: string | number = '';

        if (sortKey === 'name') {
          leftValue = String(left.name || '').toLocaleLowerCase();
          rightValue = String(right.name || '').toLocaleLowerCase();
        } else if (sortKey === 'points') {
          leftValue = Number(left.defaultHousePoints || 0);
          rightValue = Number(right.defaultHousePoints || 0);
        } else if (sortKey === 'status') {
          leftValue = left.isEnabled ? 1 : 0;
          rightValue = right.isEnabled ? 1 : 0;
        } else {
          leftValue = left.updatedAt ? new Date(left.updatedAt).getTime() : 0;
          rightValue = right.updatedAt ? new Date(right.updatedAt).getTime() : 0;
        }

        if (leftValue < rightValue) return -1 * direction;
        if (leftValue > rightValue) return 1 * direction;
        return String(left.name || '').localeCompare(String(right.name || ''));
      });
  }

  getPageItems(): AwardType[] {
    const start = (this.state.currentPage - 1) * PAGE_SIZE;
    return this.getSortedItems().slice(start, start + PAGE_SIZE);
  }

  getSelectedItems() {
    return this.state.items.filter(item => this.state.selectedIds.includes(item.id));
  }

  getDraftForCreate(): Partial<AwardType> {
    return {
      name: '',
      description: '',
      picture: '',
      defaultHousePoints: 5,
      isEnabled: true,
      allowPeerNomination: false,
      allowNegative: false,
      allowedRoles: ['ADMIN', 'MANAGER', 'TEACHER', 'COACH']
    };
  }

  icon(path: string, className?: string) {
    return (
      <svg
        className={className || 'awardTypesIcon'}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={path} />
      </svg>
    );
  }

  showToast = (toast: string) => {
    clearTimeout(this.toastTimer);
    this.setState({ toast });
    this.toastTimer = setTimeout(() => this.setState({ toast: null }), 2600);
  };

  openCreateDrawer = () => {
    this.setState({
      activeId: null,
      draft: this.getDraftForCreate(),
      isCreateMode: true,
      selectedIds: [],
      submitError: null,
      isColumnsOpen: false,
      isFiltersOpen: false
    });
  };

  openEditDrawer = (item: AwardType) => {
    this.setState({
      activeId: item.id,
      draft: {
        ...item,
        description: item.description || '',
        picture: item.picture || '',
        allowedRoles: item.allowedRoles && item.allowedRoles.length > 0 ? item.allowedRoles : ROLE_OPTIONS,
        allowPeerNomination: Boolean(item.allowPeerNomination),
        allowNegative: Boolean(item.allowNegative)
      },
      isCreateMode: false,
      submitError: null,
      isColumnsOpen: false,
      isFiltersOpen: false
    });
  };

  closeDrawer = () => {
    this.setState({ activeId: null, draft: null, isCreateMode: false, submitError: null });
  };

  setDraft = (patch: Partial<AwardType>) => {
    this.setState(state => ({
      draft: {
        ...state.draft,
        ...patch
      }
    }));
  };

  toggleRole = (role: AwardGrantRole) => {
    const draft = this.state.draft;
    if (!draft) return;

    const roles = draft.allowedRoles || [];
    const nextRoles = roles.includes(role) ? roles.filter(item => item !== role) : [...roles, role];
    this.setDraft({ allowedRoles: nextRoles });
  };

  toggleSelection = (itemId: string, checked: boolean) => {
    this.setState(state => ({
      selectedIds: checked ? [...state.selectedIds, itemId] : state.selectedIds.filter(id => id !== itemId)
    }));
  };

  clearSelection = () => this.setState({ selectedIds: [] });

  cloneFilters(filters: AwardTypeFilters): AwardTypeFilters {
    return {
      status: [...filters.status],
      points: [...filters.points],
      settings: [...filters.settings],
      roles: [...filters.roles]
    };
  }

  setSearch = (search: string): void => {
    this.setState({ search, searchDraft: search, currentPage: 1, selectedIds: [] });
  };

  openFilters = (): void => {
    this.setState({
      isFiltersOpen: true,
      isColumnsOpen: false,
      draftFilters: this.cloneFilters(this.state.filters)
    });
  };

  closeFilters = (): void => {
    this.setState({ isFiltersOpen: false, draftFilters: this.cloneFilters(this.state.filters) });
  };

  toggleDraftFilter = (field: keyof AwardTypeFilters, value: string): void => {
    this.setState(state => {
      const current = state.draftFilters[field];
      const next = current.includes(value) ? current.filter(item => item !== value) : [...current, value];
      return {
        draftFilters: {
          ...state.draftFilters,
          [field]: next
        }
      } as Pick<State, 'draftFilters'>;
    });
  };

  applyFilters = (): void => {
    this.setState(state => ({
      filters: this.cloneFilters(state.draftFilters),
      isFiltersOpen: false,
      currentPage: 1,
      selectedIds: []
    }));
  };

  clearFilters = (): void => {
    this.setState({
      filters: this.cloneFilters(EMPTY_FILTERS),
      draftFilters: this.cloneFilters(EMPTY_FILTERS),
      isFiltersOpen: false,
      currentPage: 1,
      selectedIds: []
    });
  };

  removeFilter = (field: keyof AwardTypeFilters): void => {
    this.setState(
      state =>
        ({
          filters: { ...state.filters, [field]: [] },
          draftFilters: { ...state.draftFilters, [field]: [] },
          currentPage: 1,
          selectedIds: []
        } as Pick<State, 'filters' | 'draftFilters' | 'currentPage' | 'selectedIds'>)
    );
  };

  getActiveFilterCount(): number {
    const { filters } = this.state;
    return filters.status.length + filters.points.length + filters.settings.length + filters.roles.length;
  }

  setHiddenColumns = (hiddenColumns: string[]): void => {
    const allowed = new Set(TABLE_COLUMNS.filter(column => !column.locked).map(column => column.key));
    const sanitized = hiddenColumns.filter(column => allowed.has(column as AwardColumnKey));
    saveHiddenColumns(this.props.user, COLUMN_VISIBILITY_KEY, sanitized);
    this.setState({ hiddenColumns: sanitized });
  };

  toggleColumn = (column: AwardColumnKey): void => {
    const hidden = this.state.hiddenColumns;
    this.setHiddenColumns(hidden.includes(column) ? hidden.filter(item => item !== column) : [...hidden, column]);
  };

  onSort = (sortKey: AwardSortKey): void => {
    this.setState(state => ({
      sortKey,
      sortDirection: state.sortKey === sortKey && state.sortDirection === 'ASC' ? 'DESC' : 'ASC',
      currentPage: 1
    }));
  };

  setCurrentPage = (currentPage: number): void => {
    const pageCount = Math.max(1, Math.ceil(this.getSortedItems().length / PAGE_SIZE));
    this.setState({ currentPage: Math.max(1, Math.min(currentPage, pageCount)) });
  };

  togglePageSelection = (checked: boolean): void => {
    const pageIds = this.getPageItems().map(item => item.id);
    this.setState(state => {
      const selected = new Set(state.selectedIds);
      pageIds.forEach(id => (checked ? selected.add(id) : selected.delete(id)));
      return { selectedIds: Array.from(selected) };
    });
  };

  onSearchKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.setState(state => ({ search: state.searchDraft.trim(), currentPage: 1, selectedIds: [] }));
    }
  };

  handlePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    this.setState({ isUploading: true });

    try {
      const { key } = await uploadImage(this.props.user, file);
      const rawUrl = `${window.apiImg}/images/${key}`;
      const url = ensureAbsoluteUri(rawUrl);
      this.setDraft({ picture: url });
    } finally {
      this.setState({ isUploading: false });
      event.target.value = '';
    }
  };

  validateDraft() {
    const draft = this.state.draft;
    if (!draft) return 'Award type is empty.';
    if (!draft.name || !String(draft.name).trim()) return 'Enter an award type name.';
    if (!draft.allowedRoles || draft.allowedRoles.length === 0) return 'Select at least one role.';
    return null;
  }

  buildPayload() {
    const draft = this.state.draft;
    const defaultHousePoints = Number(draft.defaultHousePoints || 0);
    const points = draft.allowNegative ? defaultHousePoints : Math.max(0, defaultHousePoints);

    return {
      name: String(draft.name || '').trim(),
      description: String(draft.description || '').trim() || undefined,
      defaultHousePoints: points,
      picture: draft.picture ? String(draft.picture).trim() : undefined,
      isEnabled: Boolean(draft.isEnabled),
      allowPeerNomination: Boolean(draft.allowPeerNomination),
      allowNegative: Boolean(draft.allowNegative),
      allowedRoles: draft.allowedRoles || []
    };
  }

  onSaveDraft = () => {
    const error = this.validateDraft();
    if (error) {
      this.setState({ submitError: error });
      return;
    }

    const { user } = this.props;
    const { activeId, isCreateMode } = this.state;
    const payload = this.buildPayload();
    const action = isCreateMode ? createAwardType(user, payload) : updateAwardType(user, payload, activeId);

    this.setState({ isSaving: true, submitError: null });

    action
      .then(saved => {
        this.setState(state => {
          const items = isCreateMode
            ? [saved, ...state.items]
            : state.items.map(item => (item.id === saved.id ? saved : item));
          return {
            items,
            activeId: saved.id,
            draft: {
              ...saved,
              description: saved.description || '',
              picture: saved.picture || '',
              allowedRoles: saved.allowedRoles && saved.allowedRoles.length > 0 ? saved.allowedRoles : ROLE_OPTIONS
            },
            isCreateMode: false,
            isSaving: false,
            selectedIds: [saved.id],
            currentPage: isCreateMode ? 1 : state.currentPage
          };
        });
        this.showToast(isCreateMode ? 'Award type created' : 'Award type saved');
      })
      .catch(err => {
        const status = err.response?.status ?? err.status;
        const msg =
          status === 409 ? 'An award type with that name already exists.' : 'Something went wrong - please try again.';
        this.setState({ isSaving: false, submitError: msg });
      });
  };

  onDeleteClick = () => {
    const ids = this.state.selectedIds;
    if (ids.length === 0) return;

    this.setState({ isSaving: true });

    BPromise.all(ids.map(id => deleteAwardType(this.props.user, id)))
      .then(() => {
        this.setState(state => ({
          items: state.items.filter(item => !ids.includes(item.id)),
          selectedIds: [],
          activeId: state.activeId && ids.includes(state.activeId) ? null : state.activeId,
          draft: state.activeId && ids.includes(state.activeId) ? null : state.draft,
          isCreateMode: false,
          isDeleteModalOpen: false,
          isSaving: false,
          currentPage: 1
        }));
        this.showToast(`${ids.length} award type${ids.length === 1 ? '' : 's'} deleted`);
      })
      .catch(() => {
        this.setState({ isSaving: false, isDeleteModalOpen: false });
        this.showToast('Award types could not be deleted. Please try again.');
      });
  };

  renderImageTile(item: Partial<AwardType>, size = 52) {
    const name = item.name || 'Award';
    const points = Number(item.defaultHousePoints || 0);
    const hue =
      Math.abs(
        String(name)
          .split('')
          .reduce((sum, char) => sum + char.charCodeAt(0), 0)
      ) % 360;
    const color = `hsl(${hue}, 58%, 42%)`;

    if (item.picture) {
      return (
        <span className="awardTypesTile" style={{ width: size, height: size }}>
          <img src={item.picture || PLACEHOLDER} alt={`${name} icon`} />
        </span>
      );
    }

    return (
      <span
        className="awardTypesTile awardTypesTile--generated"
        style={{ width: size, height: size, backgroundColor: `${color}1f`, color }}
      >
        {this.icon(
          points < 0 ? 'M5 12h14' : 'M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.8 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z'
        )}
      </span>
    );
  }

  renderPointsPill(item: Partial<AwardType>) {
    const points = Number(item.defaultHousePoints || 0);
    return (
      <span className={points < 0 ? 'awardTypesPoints awardTypesPoints--negative' : 'awardTypesPoints'}>
        {this.icon('M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.8 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z')}
        {points > 0 ? '+' : ''}
        {points} pts
      </span>
    );
  }

  renderPageHeader(): React.ReactNode {
    return (
      <header className="awardTypesPageHeader">
        <div className="awardTypesPageHeader__title">
          <h1>Award types</h1>
          <span>{this.state.items.length}</span>
        </div>
        <button type="button" className="awardTypesButton awardTypesButton--primary" onClick={this.openCreateDrawer}>
          {this.icon('M12 5v14M5 12h14')}
          Create award type
        </button>
      </header>
    );
  }

  renderToolbar(): React.ReactNode {
    const { searchDraft, isFiltersOpen, isColumnsOpen, hiddenColumns } = this.state;
    const activeFilterCount = this.getActiveFilterCount();
    const visibleColumnCount = TABLE_COLUMNS.length - hiddenColumns.length;
    const hiddenSet = new Set(hiddenColumns);

    return (
      <div className="awardTypesListToolbar">
        <label className="awardTypesSearch">
          {this.icon('M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3')}
          <input
            value={searchDraft}
            onChange={event => this.setState({ searchDraft: event.target.value })}
            onKeyDown={this.onSearchKeyDown}
            placeholder="Search by name or description"
          />
          {searchDraft ? (
            <button type="button" onClick={() => this.setSearch('')} aria-label="Clear search">
              ×
            </button>
          ) : null}
        </label>
        <button
          type="button"
          className={`awardTypesButton awardTypesButton--secondary${isFiltersOpen ? ' is-active' : ''}`}
          onClick={this.openFilters}
        >
          {this.icon('M4 6h16M7 12h10M10 18h4')}
          Filters
          {activeFilterCount > 0 ? <span className="awardTypesButton__badge">{activeFilterCount}</span> : null}
        </button>
        <div className="awardTypesColumns" ref={this.columnsRef}>
          <button
            type="button"
            className={`awardTypesButton awardTypesButton--secondary${isColumnsOpen ? ' is-active' : ''}`}
            onClick={() => this.setState({ isColumnsOpen: !isColumnsOpen, isFiltersOpen: false })}
          >
            {this.icon('M4 5h16v14H4zM10 5v14M16 5v14')}
            Columns <span className="awardTypesButton__badge">{visibleColumnCount}</span>
          </button>
          {isColumnsOpen ? (
            <div className="awardTypesColumnsMenu">
              <header>
                <strong>Show / hide columns</strong>
                <button type="button" onClick={() => this.setHiddenColumns([])}>
                  Reset
                </button>
              </header>
              {TABLE_COLUMNS.map(column => (
                <label key={column.key} className={column.locked ? 'is-locked' : ''}>
                  <input
                    type="checkbox"
                    checked={column.locked || !hiddenSet.has(column.key)}
                    disabled={column.locked}
                    onChange={() => this.toggleColumn(column.key)}
                  />
                  <span>{column.label}</span>
                  {column.locked ? <small>Required</small> : null}
                </label>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  renderFilters(): React.ReactNode {
    if (!this.state.isFiltersOpen) return null;

    const { draftFilters } = this.state;
    const statusOptions: PeopleFilterChipOption[] = [
      { value: 'enabled', label: 'Enabled' },
      { value: 'disabled', label: 'Disabled' }
    ];
    const pointOptions: PeopleFilterChipOption[] = [
      { value: 'positive', label: 'Adds points' },
      { value: 'zero', label: 'No default points' },
      { value: 'negative', label: 'Deducts points' }
    ];
    const settingOptions: PeopleFilterChipOption[] = [
      { value: 'peer', label: 'Peer nomination' },
      { value: 'negative', label: 'Negative points allowed' }
    ];
    const roleOptions: PeopleFilterChipOption[] = ROLE_OPTIONS.map((role, order) => ({
      value: role,
      label: ROLE_LABELS[role],
      order
    }));

    return (
      <div className="awardTypesFilterOverlay" onMouseDown={this.closeFilters}>
        <aside
          className="awardTypesFilterDrawer"
          aria-label="Award type filters"
          onMouseDown={event => event.stopPropagation()}
          onKeyDown={event => {
            if (event.key === 'Enter' && (event.target as HTMLElement).tagName !== 'BUTTON') {
              event.preventDefault();
              this.applyFilters();
            }
          }}
        >
          <header>
            <h2>Filters</h2>
            <button type="button" onClick={this.closeFilters} aria-label="Close filters">
              ×
            </button>
          </header>
          <div className="awardTypesFilterDrawer__body">
            <PeopleFilterChipSelect
              label="Status"
              options={statusOptions}
              selected={draftFilters.status}
              onToggle={value => this.toggleDraftFilter('status', value)}
            />
            <PeopleFilterChipSelect
              label="Effect on points"
              options={pointOptions}
              selected={draftFilters.points}
              onToggle={value => this.toggleDraftFilter('points', value)}
            />
            <PeopleFilterChipSelect
              label="Special settings"
              options={settingOptions}
              selected={draftFilters.settings}
              onToggle={value => this.toggleDraftFilter('settings', value)}
            />
            <PeopleFilterChipSelect
              label="Grantable by"
              options={roleOptions}
              selected={draftFilters.roles}
              onToggle={value => this.toggleDraftFilter('roles', value)}
            />
          </div>
          <footer>
            <button type="button" className="awardTypesButton awardTypesButton--ghost" onClick={this.clearFilters}>
              Clear all
            </button>
            <div>
              <button
                type="button"
                className="awardTypesButton awardTypesButton--secondary"
                onClick={this.closeFilters}
              >
                Cancel
              </button>
              <button type="button" className="awardTypesButton awardTypesButton--primary" onClick={this.applyFilters}>
                Apply
              </button>
            </div>
          </footer>
        </aside>
      </div>
    );
  }

  getFilterLabels(field: keyof AwardTypeFilters, values: string[]): string {
    const labels: Record<keyof AwardTypeFilters, Record<string, string>> = {
      status: { enabled: 'Enabled', disabled: 'Disabled' },
      points: { positive: 'Adds points', zero: 'No default points', negative: 'Deducts points' },
      settings: { peer: 'Peer nomination', negative: 'Negative points allowed' },
      roles: ROLE_OPTIONS.reduce((result, role) => ({ ...result, [role]: ROLE_LABELS[role] }), {})
    };
    return values.map(value => labels[field][value] || value).join(', ');
  }

  renderActiveFilters(): React.ReactNode {
    const fields: Array<{ key: keyof AwardTypeFilters; label: string }> = [
      { key: 'status', label: 'Status' },
      { key: 'points', label: 'Points' },
      { key: 'settings', label: 'Settings' },
      { key: 'roles', label: 'Grantable by' }
    ];
    const active = fields.filter(field => this.state.filters[field.key].length > 0);
    if (active.length === 0) return null;

    return (
      <div className="awardTypesActiveFilters">
        {active.map(field => (
          <span key={field.key}>
            <b>{field.label}:</b> {this.getFilterLabels(field.key, this.state.filters[field.key])}
            <button
              type="button"
              onClick={() => this.removeFilter(field.key)}
              aria-label={`Remove ${field.label} filter`}
            >
              ×
            </button>
          </span>
        ))}
        <button type="button" onClick={this.clearFilters}>
          Clear all
        </button>
      </div>
    );
  }

  formatUpdatedAt(value?: string): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }

  renderSortHeader(label: string, sortKey: AwardSortKey): React.ReactNode {
    const active = this.state.sortKey === sortKey;
    return (
      <button type="button" onClick={() => this.onSort(sortKey)}>
        {label}
        <span className={active ? 'is-active' : ''}>
          {active ? (this.state.sortDirection === 'ASC' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    );
  }

  renderTable(): React.ReactNode {
    const items = this.getPageItems();
    const hidden = new Set(this.state.hiddenColumns);
    const selected = new Set(this.state.selectedIds);
    const isFiltered = Boolean(this.state.search.trim()) || this.getActiveFilterCount() > 0;
    const allPageSelected = items.length > 0 && items.every(item => selected.has(item.id));
    const visibleDataColumns = TABLE_COLUMNS.filter(column => column.locked || !hidden.has(column.key)).length;

    return (
      <div className="awardTypesTableScroll">
        <table className="awardTypesTable">
          <thead>
            <tr>
              <th className="awardTypesTable__check">
                <SelectAllCheckbox
                  compact
                  isChecked={allPageSelected}
                  totalCount={this.getSortedItems().length}
                  currentPage={this.state.currentPage}
                  pageItemsCount={items.length}
                  selectedItems={this.getSelectedItems()}
                  onSelectAllOnPageClick={() => this.togglePageSelection(true)}
                  onSelectAllClick={() => this.setState({ selectedIds: this.getSortedItems().map(item => item.id) })}
                  onUnselectAllClick={this.clearSelection}
                />
              </th>
              <th className="awardTypesTable__award">{this.renderSortHeader('Award', 'name')}</th>
              {!hidden.has('points') ? <th>{this.renderSortHeader('Default points', 'points')}</th> : null}
              {!hidden.has('status') ? <th>{this.renderSortHeader('Status', 'status')}</th> : null}
              {!hidden.has('roles') ? <th>Grantable by</th> : null}
              {!hidden.has('settings') ? <th>Settings</th> : null}
              {!hidden.has('updated') ? <th>{this.renderSortHeader('Updated', 'updated')}</th> : null}
              <th className="awardTypesTable__open" aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const roles = item.allowedRoles || [];
              return (
                <tr
                  key={item.id}
                  className={selected.has(item.id) ? 'is-selected' : ''}
                  onClick={() => this.openEditDrawer(item)}
                  tabIndex={0}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && event.target === event.currentTarget) this.openEditDrawer(item);
                  }}
                >
                  <td className="awardTypesTable__check" onClick={event => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={event => this.toggleSelection(item.id, event.target.checked)}
                      aria-label={`Select ${item.name}`}
                    />
                  </td>
                  <td className="awardTypesTable__award">
                    <div className="awardTypesAwardCell">
                      {this.renderImageTile(item, 40)}
                      <div>
                        <strong>{item.name}</strong>
                        <small>{item.description || 'No description added yet.'}</small>
                      </div>
                    </div>
                  </td>
                  {!hidden.has('points') ? <td>{this.renderPointsPill(item)}</td> : null}
                  {!hidden.has('status') ? (
                    <td>
                      <span
                        className={item.isEnabled ? 'awardTypesStatus awardTypesStatus--enabled' : 'awardTypesStatus'}
                      >
                        <i />
                        {item.isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                  ) : null}
                  {!hidden.has('roles') ? (
                    <td>
                      <div className="awardTypesRoleList">
                        {roles.length > 0 ? (
                          roles.map(role => <span key={role}>{ROLE_LABELS[role] || role}</span>)
                        ) : (
                          <em>No roles</em>
                        )}
                      </div>
                    </td>
                  ) : null}
                  {!hidden.has('settings') ? (
                    <td>
                      <div className="awardTypesSettingsList">
                        {item.allowPeerNomination ? <span>Peer nomination</span> : null}
                        {item.allowNegative ? <span className="is-danger">Negative points</span> : null}
                        {!item.allowPeerNomination && !item.allowNegative ? <em>Standard</em> : null}
                      </div>
                    </td>
                  ) : null}
                  {!hidden.has('updated') ? (
                    <td className="awardTypesTable__date">{this.formatUpdatedAt(item.updatedAt)}</td>
                  ) : null}
                  <td className="awardTypesTable__open">
                    <span aria-hidden="true">›</span>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 ? (
              <tr>
                <td colSpan={visibleDataColumns + 2} className="awardTypesTable__empty">
                  <ListEmptyState
                    icon={faAward}
                    title={isFiltered ? 'No award types match your search' : 'No award types yet'}
                    description={
                      isFiltered
                        ? 'Try another search term or change the filters to see other award types.'
                        : 'Create an award type to define recognition, points and who can grant it.'
                    }
                  />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    );
  }

  renderPagination(): React.ReactNode {
    const total = this.getSortedItems().length;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (total === 0) return null;
    const pageWindowStart = Math.max(1, Math.min(this.state.currentPage - 2, pageCount - 4));
    const pageWindowEnd = Math.min(pageCount, pageWindowStart + 4);
    const pages = Array.from({ length: pageWindowEnd - pageWindowStart + 1 }, (_, index) => pageWindowStart + index);

    return (
      <nav className="awardTypesPagination" aria-label="Award types pages">
        <span>
          Page {this.state.currentPage} of {pageCount}
        </span>
        <button
          type="button"
          disabled={this.state.currentPage <= 1}
          onClick={() => this.setCurrentPage(this.state.currentPage - 1)}
        >
          Previous
        </button>
        {pages.map(page => (
          <button
            type="button"
            key={page}
            className={page === this.state.currentPage ? 'is-active' : ''}
            onClick={() => this.setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          disabled={this.state.currentPage >= pageCount}
          onClick={() => this.setCurrentPage(this.state.currentPage + 1)}
        >
          Next
        </button>
      </nav>
    );
  }

  renderSelectionBar() {
    const count = this.state.selectedIds.length;
    if (count === 0) return null;

    return (
      <div className="awardTypesSelectionBar">
        <div>
          <b>{count}</b> selected
        </div>
        <div className="awardTypesSelectionBar__actions">
          <button
            type="button"
            className="awardTypesButton awardTypesButton--danger"
            onClick={() => this.setState({ isDeleteModalOpen: true })}
          >
            {this.icon('M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6')}
            Delete
          </button>
          <button type="button" className="awardTypesButton awardTypesButton--ghost" onClick={this.clearSelection}>
            Clear selection
          </button>
        </div>
      </div>
    );
  }

  renderSwitch(label: string, description: string, field: keyof AwardType) {
    const draft = this.state.draft;
    const checked = Boolean(draft && draft[field]);

    return (
      <div className="awardTypesSwitchRow">
        <div>
          <div>{label}</div>
          <p>{description}</p>
        </div>
        <button
          type="button"
          className={checked ? 'awardTypesSwitch awardTypesSwitch--on' : 'awardTypesSwitch'}
          role="switch"
          aria-checked={checked}
          onClick={() => {
            const patch: any = { [field]: !checked };
            if (field === 'allowNegative' && checked && Number(draft.defaultHousePoints || 0) < 0) {
              patch.defaultHousePoints = Math.abs(Number(draft.defaultHousePoints || 0));
            }
            this.setDraft(patch);
          }}
        >
          <span />
        </button>
      </div>
    );
  }

  renderDrawer() {
    const { draft, isCreateMode, isUploading, isSaving, submitError } = this.state;
    if (!draft) return null;

    return (
      <div className="awardTypesDrawerOverlay" onMouseDown={this.closeDrawer}>
        <aside className="awardTypesDrawer" onMouseDown={event => event.stopPropagation()}>
          <div className="awardTypesDrawer__header">
            {this.renderImageTile(draft, 48)}
            <div>
              <h2>{isCreateMode ? 'New award type' : draft.name || 'Award type'}</h2>
              <p>
                {Number(draft.defaultHousePoints || 0) > 0 ? '+' : ''}
                {Number(draft.defaultHousePoints || 0)} pts · {draft.isEnabled ? 'Enabled' : 'Disabled'}
              </p>
            </div>
            <button type="button" className="awardTypesIconButton" onClick={this.closeDrawer} aria-label="Close">
              {this.icon('M18 6 6 18M6 6l12 12')}
            </button>
          </div>

          <div className="awardTypesDrawer__body">
            {submitError && <div className="awardTypesAlert awardTypesAlert--danger">{submitError}</div>}

            <div className="awardTypesPreviewCard">
              {this.renderImageTile(draft, 44)}
              <div>
                <div>{draft.name || 'Untitled award'}</div>
                {this.renderPointsPill(draft)}
              </div>
            </div>

            <div className="awardTypesField">
              <label>Award artwork</label>
              <div className="awardTypesArtworkRow">
                {this.renderImageTile(draft, 70)}
                <div>
                  <label className="awardTypesUploadButton">
                    {this.icon('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12')}
                    {isUploading ? 'Uploading...' : draft.picture ? 'Replace picture' : 'Upload picture'}
                    <input type="file" accept="image/png,image/jpeg" onChange={this.handlePictureChange} />
                  </label>
                  {draft.picture && (
                    <button
                      type="button"
                      className="awardTypesRemoveImage"
                      onClick={() => this.setDraft({ picture: '' })}
                    >
                      Remove picture
                    </button>
                  )}
                  <p>PNG or JPG, square works best.</p>
                </div>
              </div>
            </div>

            <div className="awardTypesField">
              <label>Name</label>
              <input
                className="awardTypesInput"
                value={draft.name || ''}
                onChange={event => this.setDraft({ name: event.target.value })}
                placeholder="e.g. Effort"
              />
            </div>

            <div className="awardTypesField">
              <label>Description</label>
              <textarea
                className="awardTypesInput awardTypesTextarea"
                value={draft.description || ''}
                onChange={event => this.setDraft({ description: event.target.value })}
                placeholder="Describe when staff should grant this award"
              />
            </div>

            <div className="awardTypesField">
              <label>Default house points</label>
              <div className="awardTypesStepper">
                <button
                  type="button"
                  onClick={() => this.setDraft({ defaultHousePoints: Number(draft.defaultHousePoints || 0) - 1 })}
                >
                  {this.icon('M5 12h14')}
                </button>
                <input
                  type="number"
                  value={Number(draft.defaultHousePoints || 0)}
                  onChange={event => this.setDraft({ defaultHousePoints: Number(event.target.value) })}
                />
                <button
                  type="button"
                  onClick={() => this.setDraft({ defaultHousePoints: Number(draft.defaultHousePoints || 0) + 1 })}
                >
                  {this.icon('M12 5v14M5 12h14')}
                </button>
              </div>
            </div>

            <div className="awardTypesSwitchGroup">
              {this.renderSwitch('Enabled', 'Available for staff to grant.', 'isEnabled')}
              {this.renderSwitch('Allow peer nomination', 'Students can nominate each other.', 'allowPeerNomination')}
              {this.renderSwitch('Allow negative points', 'Can subtract points where needed.', 'allowNegative')}
            </div>

            <div className="awardTypesField">
              <label>Grantable by roles</label>
              <div className="awardTypesRolesGrid">
                {ROLE_OPTIONS.map(role => {
                  const checked = Boolean(draft.allowedRoles && draft.allowedRoles.includes(role));
                  return (
                    <button
                      key={role}
                      type="button"
                      className={checked ? 'awardTypesRoleChip awardTypesRoleChip--selected' : 'awardTypesRoleChip'}
                      onClick={() => this.toggleRole(role)}
                    >
                      <span>{checked ? this.icon('M20 6 9 17l-5-5') : null}</span>
                      {ROLE_LABELS[role]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="awardTypesDrawer__footer">
            {!isCreateMode && (
              <button
                type="button"
                className="awardTypesButton awardTypesButton--danger"
                onClick={() => this.setState({ selectedIds: [this.state.activeId], isDeleteModalOpen: true })}
              >
                {this.icon('M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6')}
                Delete
              </button>
            )}
            <button
              type="button"
              className="awardTypesButton awardTypesButton--primary"
              disabled={isSaving || isUploading}
              onClick={this.onSaveDraft}
            >
              {this.icon('M20 6 9 17l-5-5')}
              {isSaving ? 'Saving...' : 'Save award type'}
            </button>
          </div>
        </aside>
      </div>
    );
  }

  renderDeleteModal() {
    const { isDeleteModalOpen, selectedIds, isSaving } = this.state;
    if (!isDeleteModalOpen) return null;

    const selectedItems = this.getSelectedItems();
    const names = selectedItems
      .slice(0, 3)
      .map(item => item.name)
      .join(', ');

    return (
      <div className="awardTypesModalOverlay">
        <div className="awardTypesModal">
          <button
            type="button"
            className="awardTypesModal__close"
            onClick={() => this.setState({ isDeleteModalOpen: false })}
            aria-label="Close"
          >
            {this.icon('M18 6 6 18M6 6l12 12')}
          </button>
          <div className="awardTypesModal__icon">
            {this.icon(
              'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01'
            )}
          </div>
          <h2>
            Delete {selectedIds.length} award type{selectedIds.length === 1 ? '' : 's'}?
          </h2>
          <p>
            This cannot be undone. Points already granted under{' '}
            {selectedIds.length === 1 ? 'this award type' : 'these award types'} stay on the House totals.
          </p>
          {names && (
            <div className="awardTypesModal__names">
              {names}
              {selectedItems.length > 3 ? ` +${selectedItems.length - 3} more` : ''}
            </div>
          )}
          <div className="awardTypesModal__actions">
            <button
              type="button"
              className="awardTypesButton awardTypesButton--ghost"
              onClick={() => this.setState({ isDeleteModalOpen: false })}
            >
              Cancel
            </button>
            <button
              type="button"
              className="awardTypesButton awardTypesButton--danger"
              disabled={isSaving}
              onClick={this.onDeleteClick}
            >
              {this.icon('M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6')}
              {isSaving ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderToast() {
    if (!this.state.toast) return null;
    return (
      <div className="awardTypesToast">
        {this.icon('M20 6 9 17l-5-5')}
        {this.state.toast}
      </div>
    );
  }

  render() {
    if (this.state.isLoading) return <Loader />;

    if (this.state.loadError) {
      return (
        <div className="awardTypesPage">
          {this.renderPageHeader()}
          <div className="awardTypesShell">
            <div className="awardTypesEmpty" role="alert">
              <div>
                {this.icon(
                  'M12 9v4M12 17h.01M10.3 3.6 2.2 18a2 2 0 0 0 1.7 3h16.2a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0z'
                )}
              </div>
              <h2>Could not load award types</h2>
              <p>{this.state.loadError}</p>
              <button type="button" className="awardTypesButton awardTypesButton--primary" onClick={this.setItems}>
                {this.icon('M20 11a8.1 8.1 0 1 1-2.4-5.7L20 7M20 2v5h-5')}
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="awardTypesPage">
        {this.renderPageHeader()}
        <div className="awardTypesShell">
          <section className="awardTypesListCard">
            {this.renderToolbar()}
            {this.renderActiveFilters()}
            {this.renderSelectionBar()}
            {this.renderTable()}
            {this.renderPagination()}
          </section>
        </div>

        {this.renderFilters()}
        {this.renderDrawer()}
        {this.renderDeleteModal()}
        {this.renderToast()}
      </div>
    );
  }
}
